import { MedplumClient } from "@medplum/core";
import { PlanDefinition, Reference } from "@medplum/fhirtypes";

import { setAccounts } from "@/utils/accounts";
import { getOrganizationReference, joinReferenceString } from "@/utils/medplum";

import { ChecklistTemplateShareSettingsOptions } from "../constants/checklist-template";
import { createTemplatePlanDefinition } from "../utils/template";
import { ChecklistTemplateFormType } from "../zod-schema/checklist-template-zod";

type CreateChecklistTemplateProps = {
  medplum: MedplumClient;
  values: ChecklistTemplateFormType;
  practiceId: string;
  practitionerId: string;
  templateId?: string;
};

/**
 * Accounts required on the write payload so Medplum access-policy checks pass.
 * Private templates need the practitioner compartment before save; public templates
 * only need the organization compartment. setAccounts still runs afterward as the
 * canonical linkage step (same pattern as lab order PlanDefinitions).
 */
const getChecklistTemplateAccountsForAccess = (
  practiceId: string,
  practitionerId: string,
  shareSettings: ChecklistTemplateShareSettingsOptions,
): Reference[] => {
  const accounts: Reference[] = [{ reference: getOrganizationReference(practiceId) }];

  if (shareSettings === ChecklistTemplateShareSettingsOptions.Private) {
    accounts.push({ reference: joinReferenceString("Practitioner", practitionerId) });
  }

  return accounts;
};

export const createChecklistTemplate = async ({
  medplum,
  values,
  practiceId,
  practitionerId,
  templateId,
}: CreateChecklistTemplateProps) => {
  const templatePayload = createTemplatePlanDefinition(values, practitionerId, templateId);
  const accountsForAccess = getChecklistTemplateAccountsForAccess(
    practiceId,
    practitionerId,
    values.shareSettings,
  );

  let savedTemplate: PlanDefinition;

  if (templateId) {
    const existingTemplate = await medplum.readResource("PlanDefinition", templateId);
    savedTemplate = await medplum.updateResource({
      ...existingTemplate,
      ...templatePayload,
      id: templateId,
      meta: {
        ...existingTemplate.meta,
        author: templatePayload.meta?.author,
        accounts: accountsForAccess,
      },
    });
  } else {
    savedTemplate = await medplum.createResource({
      ...templatePayload,
      meta: {
        ...templatePayload.meta,
        accounts: accountsForAccess,
      },
    });
  }

  await setAccounts(medplum, savedTemplate, [
    { reference: getOrganizationReference(practiceId) },
    { reference: joinReferenceString("Practitioner", practitionerId || "") },
  ]);

  return savedTemplate;
};

export const softDeleteChecklistTemplate = async (
  medplum: MedplumClient,
  template: PlanDefinition,
) => {
  return await medplum.updateResource({ ...template, status: "retired" });
};
